import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { DataResult } from '@progress/kendo-data-query';
import { catchError, concat, concatMap, exhaustMap, forkJoin, Observable, of } from 'rxjs';
import { FetchArgs } from 'src/app/shared/models/core';
import { IntegrationMappingHttpResponse } from '../../enums/mapping-http-response.enum';
import { IntegrationLookup } from '../../models/integration-lookup.model';
import { IntegrationMappingsService } from '../../services/integration-mappings.service';
import { IntegrationMappingsUtility } from '../utilities/integration-mappings.utility';
import { IntegrationMappingsActions } from './integration-mappings.actions';
import { HttpResponseStatus, integrationMappingsInitialState } from './integration-mappings.reducer';
import { IntegrationManagerService } from '../../services/integration-manager.service';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';

@Injectable()
export class IntegrationMappingsEffects {
    getAllIntegrationMappings$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(IntegrationMappingsActions.getAllIntegrationMappings),
            exhaustMap((action) => this.getAllIntegrationMappings(action.args)),
            concatMap((mappings) =>
                of(IntegrationMappingsActions.setAllIntegrationMappings({ integrationMappingsList: mappings }))
            )
        );
    });

    getIntegrationMappingsLookup$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(IntegrationMappingsActions.getIntegrationMappingsLookup),
            exhaustMap(() =>
                forkJoin({
                    mappings$: this.getAllIntegrationMappings(IntegrationMappingsUtility.defaultFetchArgs),
                    lookup$: this.getIntegrationMappingsLookup()
                })
            ),
            concatMap(({ mappings$, lookup$ }) =>
                of(
                    IntegrationMappingsActions.setIntegrationMappingsLookup({
                        integrationMappingsLookup: lookup$,
                        integrationMappingsList: mappings$
                    })
                )
            )
        );
    });

    addIntegrationMapping$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(IntegrationMappingsActions.addIntegrationMapping),
            exhaustMap((action) =>
                this.callHttpRequest(
                    this.mappingsService.add(action.integration),
                    IntegrationMappingHttpResponse.SuccessAdd
                )
            )
        );
    });

    updateIntegrationMapping$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(IntegrationMappingsActions.updateIntegrationMapping),
            exhaustMap((action) =>
                this.callHttpRequest(
                    this.mappingsService.update(action.integration),
                    IntegrationMappingHttpResponse.SuccessUpdate
                )
            )
        );
    });

    deleteIntegrationMapping$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(IntegrationMappingsActions.deleteIntegrationMapping),
            exhaustMap((action) =>
                this.callHttpRequest(
                    this.mappingsService.delete(action.id),
                    IntegrationMappingHttpResponse.SuccessDelete
                )
            )
        );
    });

    setResponseImportFailure$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(IntegrationMappingsActions.setCrudResponse),
            concatMap((resp) => {
                return concat(
                    of(showToasterMessage({ message: resp.response.message })),
                    of(IntegrationMappingsActions.setStatus({ status: resp.response.status }))
                );
            })
        );
    });

    private readonly managerService = inject(IntegrationManagerService);
    private readonly mappingsService = inject(IntegrationMappingsService);

    constructor(private readonly actions$: Actions) {}

    getAllIntegrationMappings = (args: FetchArgs): Observable<DataResult> =>
        this.mappingsService
            .getAllIntegrationMappings(args)
            .pipe(catchError(() => of(integrationMappingsInitialState.integrationMappingsList)));

    getIntegrationMappingsLookup = (): Observable<IntegrationLookup> =>
        this.managerService
            .getIntegrationMappingsLookup()
            .pipe(catchError(() => of(integrationMappingsInitialState.integrationMappingsLookup)));

    getValidationProblem = (error: any): string => error?.error?.detail ?? IntegrationMappingHttpResponse.Error;

    callHttpRequest(httpResult$: Observable<any>, message: string): Observable<any> {
        return concat(
            of(IntegrationMappingsActions.setIsSaving({ isSaving: true })),
            httpResult$.pipe(
                concatMap(() =>
                    of(
                        IntegrationMappingsActions.setCrudResponse({
                            response: {
                                message,
                                status: HttpResponseStatus.Success
                            }
                        })
                    )
                ),
                catchError((error: unknown) => {
                    return concat(
                        of(
                            IntegrationMappingsActions.setCrudResponse({
                                response: {
                                    message: this.getValidationProblem(error),
                                    status: HttpResponseStatus.Failed
                                }
                            })
                        )
                    );
                })
            ),
            of(IntegrationMappingsActions.setIsSaving({ isSaving: false }))
        );
    }
}
